import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

/**
 * Latency adjustment control with decrease/increase buttons.
 *
 * @fires latency-change - Fired when latency changes (detail: { latency: number })
 */
@customElement('latency-control')
export class LatencyControl extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                flex-wrap: wrap;
                justify-content: center;
            }

            .label {
                color: var(--text-secondary);
                font-size: 0.875rem;
                font-weight: 500;
            }

            .value {
                color: var(--text-primary);
                font-size: 0.875rem;
                font-weight: 600;
                min-width: 3rem;
                text-align: center;
                padding: 0.25rem 0.5rem;
                background-color: var(--surface-light);
                border-radius: 0.25rem;
            }

            .btn-latency {
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: var(--surface-light);
                color: var(--text-primary);
                border: 2px solid var(--border);
                border-radius: 0.375rem;
                font-size: 1.25rem;
                font-weight: 700;
                cursor: pointer;
                transition: all 0.2s;
                user-select: none;
                -webkit-tap-highlight-color: transparent;
            }

            .btn-latency:hover {
                background-color: var(--primary-color);
                border-color: var(--primary-color);
                transform: translateY(-2px);
            }

            .btn-latency:active {
                transform: translateY(0);
            }
        `,
    ];

    @property({ type: Number })
    latency = 0;

    @property({ type: Number })
    min = -1000;

    @property({ type: Number })
    max = 2000;

    @property({ type: Number })
    step = 250;

    private get _displayValue(): string {
        return (this.latency / 1000).toFixed(1) + 's';
    }

    private _decrease(): void {
        const newLatency = Math.max(this.min, this.latency - this.step);
        this._fireChange(newLatency);
    }

    private _increase(): void {
        const newLatency = Math.min(this.max, this.latency + this.step);
        this._fireChange(newLatency);
    }

    private _fireChange(latency: number): void {
        this.latency = latency;
        this.dispatchEvent(
            new CustomEvent('latency-change', {
                bubbles: true,
                composed: true,
                detail: { latency },
            })
        );
    }

    render() {
        return html`
            <span class="label">Latency:</span>
            <button class="btn-latency" @click=${this._decrease}>${'\u2212'}</button>
            <span class="value">${this._displayValue}</span>
            <button class="btn-latency" @click=${this._increase}>+</button>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'latency-control': LatencyControl;
    }
}
